import type { GlobalFieldId } from '#layers/data-model/types/schema/ids';
import type { AnyResolvedField, ResolvedMonospaceCollection } from '#layers/data-model/types/schema/resolved-schema';
import type { QuerySchemaCollection, QuerySchemaOutput, QuerySchemaWalker } from './query-schema';
import { findCollectionFieldById } from '#layers/data-model/utils/fields/fields';
import { isPrimitiveField } from '#layers/data-model/utils/schema/collection';
import { mapQueryCapability, resolveCollectionQueryCapability, supportedSurface } from './query-capability';

/**
 * The read that loaded a collection's top-level rows.
 *
 * Rows a relation includes are always `Many`: the engine returns an include with its target's list output.
 */
export const CollectionReads = {
	/** The filtered list read, as listings and relation inputs load rows. */
	Many: 'many',
	/** The by-key read, as item pages load their row. */
	One: 'one',
} as const;

export type CollectionRead = (typeof CollectionReads)[keyof typeof CollectionReads];

const READ_OUTPUTS = {
	[CollectionReads.Many]: (api) => api.readMany()?.output(),
	[CollectionReads.One]: (api) => api.readOne()?.output(),
} satisfies Record<CollectionRead, (api: QuerySchemaCollection) => QuerySchemaOutput | undefined>;

/**
 * The fields `read` returns of `collection`'s rows, which requests for them are narrowed to.
 *
 * `undefined` where the query schema does not gate the read's fields and requests are not narrowed:
 * system collections, a schema that is not loaded, and a collection without that read.
 */
export function resolveReadOutput(
	collection: Pick<ResolvedMonospaceCollection, 'id' | 'isSystem'>,
	read: CollectionRead,
	schema: QuerySchemaWalker | null | undefined,
): QuerySchemaOutput | undefined {
	return supportedSurface(mapQueryCapability(resolveCollectionQueryCapability(collection, schema), READ_OUTPUTS[read]));
}

/**
 * Whether `output` returns the field at `fieldPath` of `collection`, following each relation into
 * the output it returns. A relation at the end of the path is returned when the output returns it at all.
 */
export function outputReturnsFieldPath(output: QuerySchemaOutput, collection: ResolvedMonospaceCollection, fieldPath: readonly GlobalFieldId[]): boolean {
	let surface: QuerySchemaOutput | undefined = output;
	let current: ResolvedMonospaceCollection | undefined = collection;

	for (const [index, fieldId] of fieldPath.entries()) {
		if (!surface || !current) return false;
		const field: AnyResolvedField | undefined = findCollectionFieldById(current, fieldId);
		if (!field) return false;
		if (isPrimitiveField(field)) return index === fieldPath.length - 1 && surface.primitiveFieldById(field.id);
		surface = surface.relationFieldById(field.id);
		current = field.oppositeCollection;
	}

	return fieldPath.length > 0 && surface !== undefined;
}
